// Helper pour vérifier les permissions basées sur le plan utilisateur

// Plans et leurs capacités
const PLAN_FEATURES = {
    free: {
        mentions: {
            todoToMessage: false,    // Aucune mention @ autorisée
            messageToTodo: false,    // Aucune mention @ autorisée
            userMentions: false,     // Aucune mention @ autorisée
            userNotifications: false, // Pas de notifications utilisateur
            userEmailNotifications: false, // Pas d'emails de notification
            notifications: false    // Page notifications cachée
        },
        exports: false,
        imports: false,
        bulkDelete: false,      // Suppression multiple non autorisée
        bulkDuplicate: false,   // Duplication multiple non autorisée
        messageSettings: false, // Paramètres des messages non disponibles
        templates: false,       // Modèles de messages non disponibles
        dateCommands: false,    // Dates dynamiques non disponibles
        description: "1 utilisateur, 30 messages, 15 todos, aucune mention @, support communauté"
    },
    starter: {
        mentions: {
            todoToMessage: true,     // Mentions @ vers messages/todos autorisées
            messageToTodo: true,     // Mentions @ vers messages/todos autorisées
            userMentions: false,     // Mentions @ vers utilisateurs pas encore
            userNotifications: false, // Pas encore de notifications utilisateur
            userEmailNotifications: false, // Pas d'emails de notification
            notifications: false    // Page notifications pas encore accessible
        },
        exports: false,
        imports: false,
        bulkDelete: true,       // Suppression multiple autorisée pour admins/owners
        bulkDuplicate: true,    // Duplication multiple autorisée pour admins/owners
        messageSettings: true,  // Paramètres des messages disponibles
        templates: false,       // Modèles de messages non disponibles
        dateCommands: true      // Dates dynamiques disponibles
    },
    team: {
        mentions: {
            todoToMessage: true,
            messageToTodo: true,
            userMentions: true,      // Mentions @ vers utilisateurs autorisées
            userNotifications: true, // Notifications in-app pour utilisateurs
            userEmailNotifications: false, // Pas encore d'emails
            notifications: true     // Page notifications accessible
        },
        exports: true,              // Export CSV/PDF/JSON activé
        imports: true,              // Import CSV/PDF/JSON activé
        bulkDelete: true,          // Suppression multiple autorisée pour admins/owners
        bulkDuplicate: true,       // Duplication multiple autorisée pour admins/owners
        messageSettings: true,      // Paramètres des messages disponibles (hérité de Starter)
        customTabs: true,          // Onglets personnalisés disponibles
        templates: true,           // Modèles de messages disponibles
        dateCommands: true         // Dates dynamiques disponibles
    },
    business: {
        mentions: {
            todoToMessage: true,
            messageToTodo: true,
            userMentions: true,
            userNotifications: true,
            userEmailNotifications: true, // Notifications par email disponibles
            notifications: true
        },
        exports: true,              // Export CSV/PDF/JSON activé
        imports: true,              // Import CSV/PDF/JSON activé
        bulkDelete: true,          // Suppression multiple autorisée pour admins/owners
        bulkDuplicate: true,       // Duplication multiple autorisée pour admins/owners
        messageSettings: true,      // Paramètres des messages disponibles
        customTabs: true,          // Onglets personnalisés disponibles
        templates: true,           // Modèles de messages disponibles
        dateCommands: true         // Dates dynamiques disponibles
    },
    enterprise: {
        mentions: {
            todoToMessage: true,
            messageToTodo: true,
            userMentions: true,
            userNotifications: true,
            userEmailNotifications: true, // Notifications par email disponibles
            notifications: true
        },
        exports: true,
        imports: true,
        bulkDelete: true,          // Suppression multiple autorisée pour admins/owners
        bulkDuplicate: true,       // Duplication multiple autorisée pour admins/owners
        messageSettings: true,      // Paramètres des messages disponibles
        customTabs: true,          // Onglets personnalisés disponibles
        templates: true,           // Modèles de messages disponibles
        customBranding: true,      // Personnalisation de la marque (logo → texte + police)
        dateCommands: true         // Dates dynamiques disponibles
    }
};

// Fonction utilitaire pour vérifier si un plan permet les custom tabs
export const hasCustomTabsPermission = (plan) => {
    return ['team', 'business', 'enterprise'].includes(plan);
};

// Hook pour vérifier les permissions du plan actuel
export const usePlanPermissions = (currentPlan = 'free') => {
    const planFeatures = PLAN_FEATURES[currentPlan] || PLAN_FEATURES.free;
    
    return {
        // Mentions
        canMentionUsers: planFeatures.mentions.userMentions,
        canMentionTodos: planFeatures.mentions.todoToMessage,
        canMentionMessages: planFeatures.mentions.messageToTodo,
        canSendUserNotifications: planFeatures.mentions.userNotifications,
        canSendEmailNotifications: planFeatures.mentions.userEmailNotifications,
        showNotifications: planFeatures.mentions.notifications,
        
        // Import/Export
        canExport: planFeatures.exports,
        canImport: planFeatures.imports,
        
        // Bulk operations
        canBulkDelete: planFeatures.bulkDelete,
        canBulkDuplicate: planFeatures.bulkDuplicate || false,
        
        // Message settings
        canUseMessageSettings: planFeatures.messageSettings || false,
        
        // Custom tabs
        canUseCustomTabs: planFeatures.customTabs || false,
        
        // Templates
        canUseTemplates: planFeatures.templates || false,

        // Custom branding (Enterprise)
        canUseCustomBranding: planFeatures.customBranding || false,

        // Dates dynamiques
        canUseDateCommands: planFeatures.dateCommands || false,
        
        // Helper pour vérifier une permission spécifique
        hasPermission: (feature) => {
            switch (feature) {
                case 'userMentions': return planFeatures.mentions.userMentions;
                case 'todoToMessage': return planFeatures.mentions.todoToMessage;
                case 'messageToTodo': return planFeatures.mentions.messageToTodo;
                case 'userNotifications': return planFeatures.mentions.userNotifications;
                case 'emailNotifications': return planFeatures.mentions.userEmailNotifications;
                case 'notifications': return planFeatures.mentions.notifications;
                case 'exports': return planFeatures.exports;
                case 'imports': return planFeatures.imports;
                case 'bulkDelete': return planFeatures.bulkDelete || false;
                case 'bulkDuplicate': return planFeatures.bulkDuplicate || false;
                case 'messageSettings': return planFeatures.messageSettings || false;
                case 'customTabs': return planFeatures.customTabs || false;
                case 'templates': return planFeatures.templates || false;
                case 'customBranding': return planFeatures.customBranding || false;
                case 'dateCommands': return planFeatures.dateCommands || false;
                default: return false;
            }
        },
        
        // Message d'upgrade pour une fonctionnalité
        getUpgradeMessage: (feature) => {
            const messages = {
                todoToMessage: "Les mentions @ vers messages et todos nécessitent le plan Starter minimum",
                messageToTodo: "Les mentions @ vers messages et todos nécessitent le plan Starter minimum",
                userMentions: "Les mentions @ d'utilisateurs nécessitent le plan Team minimum",
                userNotifications: "Les notifications utilisateur nécessitent le plan Team minimum",
                emailNotifications: "Les notifications par email nécessitent le plan Business minimum",
                notifications: "Les notifications nécessitent le plan Team minimum", 
                exports: "Les exports nécessitent le plan Team minimum",
                imports: "Les imports nécessitent le plan Team minimum",
                bulkDelete: "La suppression multiple nécessite le plan Starter minimum et les permissions d'administration",
                bulkDuplicate: "La duplication multiple nécessite le plan Starter minimum et les permissions d'administration",
                customTabs: "Les onglets personnalisés nécessitent le plan Team minimum",
                templates: "Les modèles de messages nécessitent le plan Team minimum",
                dateCommands: "Les dates dynamiques (//Mois-1, //Année+1…) nécessitent le plan Starter minimum"
            };
            return messages[feature] || "Cette fonctionnalité nécessite un plan supérieur";
        },
        
        // Plan minimum requis pour une fonctionnalité
        getRequiredPlan: (feature) => {
            const requirements = {
                todoToMessage: 'starter',
                messageToTodo: 'starter',
                userMentions: 'team',
                userNotifications: 'team',
                emailNotifications: 'business',
                notifications: 'team',
                exports: 'team',
                imports: 'team',
                bulkDelete: 'starter',
                bulkDuplicate: 'starter',
                customTabs: 'team',
                templates: 'team',
                dateCommands: 'starter'
            };
            return requirements[feature] || 'team';
        }
    };
};

// Helper pour obtenir les infos du plan depuis le contexte utilisateur
export const getCurrentPlan = (user, activeTenantId) => {
    if (!user || !activeTenantId) return 'free';

    const tenant = user.tenants?.find(t =>
        t.tenantId === activeTenantId ||
        t.tenantId?.toString() === activeTenantId ||
        t._id === activeTenantId ||
        t._id?.toString() === activeTenantId
    );
    return tenant?.plan || 'free';
};

// Composant pour afficher un message d'upgrade
export const UpgradeMessage = ({ feature, currentPlan, onUpgrade, className = '' }) => {
    const { getUpgradeMessage, getRequiredPlan } = usePlanPermissions(currentPlan);
    const requiredPlan = getRequiredPlan(feature);
    
    return (
        <div className={`p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg ${className}`}>
            <div className="flex items-start gap-2">
                <div className="flex-1">
                    <p className="text-sm text-orange-800 dark:text-orange-200">
                        {getUpgradeMessage(feature)}
                    </p>
                </div>
                {onUpgrade && (
                    <button
                        onClick={() => onUpgrade(requiredPlan)}
                        className="px-3 py-1 text-xs bg-orange-600 hover:bg-orange-700 text-white rounded transition-colors"
                    >
                        Upgrader
                    </button>
                )}
            </div>
        </div>
    );
};

export default PLAN_FEATURES;